/**
 * <s-help-doc> — Help Center guide enhancements.
 *
 * - Builds "On this page" from the guide's h2/h3 headings into every
 *   [data-help-toc] list and highlights the heading in view.
 * - Adds a copy-link button to each heading.
 * - "Was this page helpful?" vote buttons ([data-help-vote]) remember the
 *   answer per guide and reveal [data-help-thanks].
 * - Records the guide in "Recently viewed" (localStorage) from
 *   data-title / data-url.
 * - Wraps wide tables so they scroll instead of overflowing.
 */
const RECENT_KEY = 'the4-help-recent';
const VOTE_KEY = 'the4-help-votes';

const slug = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'section';

class SHelpDoc extends HTMLElement {
  #ac;
  #io;

  connectedCallback() {
    this.#ac = new AbortController();
    const content = this.querySelector('[data-help-content]');
    if (content) {
      this.#tidy(content);
      this.#buildToc(content);
    }
    this.#setupVotes();
    this.#record();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#io?.disconnect();
  }

  #tidy(content) {
    content.querySelectorAll('table').forEach((t) => {
      if (t.parentElement?.classList.contains('help-table')) return;
      const wrap = document.createElement('div');
      wrap.className = 'help-table';
      t.replaceWith(wrap);
      wrap.append(t);
    });
    content.querySelectorAll('img').forEach((img) => {
      img.loading = 'lazy';
      img.decoding = 'async';
    });
  }

  #buildToc(content) {
    const headings = [...content.querySelectorAll('h2, h3')].filter((h) => h.textContent.trim());
    const lists = this.querySelectorAll('[data-help-toc]');
    const tocWraps = this.querySelectorAll('[data-help-toc-wrap]');
    if (headings.length < 2) {
      tocWraps.forEach((w) => w.setAttribute('hidden', ''));
      return;
    }

    const used = new Set();
    const copyLabel = this.dataset.copyLabel || '';
    const copiedLabel = this.dataset.copiedLabel || '';
    headings.forEach((h) => {
      let id = h.id && !used.has(h.id) ? h.id : slug(h.textContent);
      while (used.has(id)) id += '-1';
      used.add(id);
      h.id = id;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'help-anchor';
      btn.setAttribute('aria-label', copyLabel);
      btn.textContent = '#';
      btn.addEventListener('click', async () => {
        const url = `${location.origin}${location.pathname}#${encodeURIComponent(id)}`;
        history.replaceState(null, '', `#${encodeURIComponent(id)}`);
        try { await navigator.clipboard.writeText(url); } catch { /* clipboard unavailable */ }
        btn.dataset.copied = '';
        btn.setAttribute('aria-label', copiedLabel);
        setTimeout(() => { delete btn.dataset.copied; btn.setAttribute('aria-label', copyLabel); }, 1600);
      }, { signal: this.#ac.signal });
      h.append(btn);
    });

    const html = headings.map((h) => {
      const text = (h.firstChild ? [...h.childNodes].filter((n) => !(n instanceof HTMLButtonElement)).map((n) => n.textContent).join('') : h.textContent).trim();
      const safe = text.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
      return `<li><a href="#${encodeURIComponent(h.id)}" class="help-toc-link" data-level="${h.tagName === 'H3' ? 3 : 2}" data-target="${h.id}">${safe}</a></li>`;
    }).join('');
    lists.forEach((l) => { l.innerHTML = html; });

    const links = this.querySelectorAll('.help-toc-link');
    const setActive = (id) => links.forEach((a) => a.toggleAttribute('data-active', a.dataset.target === id));
    const visible = new Map();
    this.#io = new IntersectionObserver((entries) => {
      entries.forEach((en) => visible.set(en.target.id, en.isIntersecting));
      const first = headings.find((h) => visible.get(h.id));
      if (first) setActive(first.id);
    }, { rootMargin: '-96px 0px -60% 0px' });
    headings.forEach((h) => this.#io.observe(h));
    setActive(headings[0].id);

    // Close the mobile "On this page" disclosure after a jump.
    this.querySelectorAll('[data-help-toc-mobile] a').forEach((a) => a.addEventListener('click', () => {
      a.closest('details')?.removeAttribute('open');
    }, { signal: this.#ac.signal }));
  }

  #votes() {
    try { return JSON.parse(localStorage.getItem(VOTE_KEY) || '{}'); } catch { return {}; }
  }

  #setupVotes() {
    const key = this.dataset.handle;
    const buttons = this.querySelectorAll('[data-help-vote]');
    const thanks = this.querySelector('[data-help-thanks]');
    if (!buttons.length || !key) return;
    const show = (vote) => {
      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.helpVote === vote)));
      if (thanks) thanks.hidden = !vote;
    };
    show(this.#votes()[key]);
    buttons.forEach((b) => b.addEventListener('click', () => {
      const all = this.#votes();
      all[key] = b.dataset.helpVote;
      try { localStorage.setItem(VOTE_KEY, JSON.stringify(all)); } catch { /* storage unavailable */ }
      show(all[key]);
    }, { signal: this.#ac.signal }));
  }

  #record() {
    const { title, url } = this.dataset;
    if (!title || !url) return;
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').filter((r) => r.u !== url);
      list.unshift({ t: title, u: url });
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 6)));
    } catch { /* storage unavailable */ }
  }
}

customElements.define('s-help-doc', SHelpDoc);
